import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {combineLatest, distinctUntilChanged} from 'rxjs';
import {AlarmService} from '../../../services/alarm.service';
import {DeviceService} from '../../../services/device.service';
import {DataviewComponent} from '../../../shared/components/dataview-component/dataview-component';
import {formatTimeAgoTranslation} from '../../../shared/utils/date.util';
import { getMarkerStyle, getMarkerStyleFromAlarm } from '../../../shared/utils/map/map-marker.util';
import {AlarmDto} from '../../../types/dtos/alarm.dto';
import {DeviceDto} from '../../../types/dtos/device.dto';
import {AlarmStatus, AlarmType} from '../../../types/enums/alarm.enum';
import {DeviceStatus} from '../../../types/enums/device.enum';
import {AlarmDataviewRow} from '../../../types/interfaces/alarm.interfaces';
import {DataviewConfig, DataviewRowInterface} from '../../../types/interfaces/input.interface';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-current-alarm',
  imports: [
    DataviewComponent,
  ],
  templateUrl: './current-alarm.component.html',
  styleUrl: './current-alarm.component.css',
})
export class CurrentAlarmComponent implements OnInit {

  public loading: boolean = false;

  public dataViewRow: DataviewRowInterface<AlarmDataviewRow>[] = [];
  public dataViewConfig: DataviewConfig<AlarmDataviewRow>;
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly _alarmService: AlarmService,
    private readonly _deviceService: DeviceService,
    private readonly _router: Router,
    private readonly _translateService: TranslateService,
  ) {
    this.dataViewConfig = {
      emptyMessage: this._translateService.instant('common.message.no-devices-available'),
      content: [
        {
          class: 'gray serial-number',
          key: 'serialNumber',
        },
        {
          key: 'title',
          class: 'title',
        },
        {
          key: 'created_at',
          class: 'date',
        },
      ],
      emptyIcon: 'check_circle',
      rowAction: (alarm: AlarmDataviewRow) => {
        this._router.navigate([
          '/alarms',
        ], {
          queryParams: {
            id: alarm.id,
          },
        }).then();
      },
    };
  }

  public ngOnInit(): void {
    this.loadCurrentAlarms();
  }

  private loadCurrentAlarms(): void {
    this.loading = true;

    const alarmTypeFilter$ = this._alarmService.alarmTypeFilter$.pipe(
      distinctUntilChanged((prev: AlarmType[], curr: AlarmType[]) =>
        prev.length !== curr.length
      ),
    );

    combineLatest([
      this._alarmService.unResolvedAlarms$,
      this._deviceService.devices$,
      alarmTypeFilter$,
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: ([alarmResponse, devices, alarmTypeFilter]: [AlarmDto[], DeviceDto[], AlarmType[]]) => {
          let dataViewRowNew: DataviewRowInterface<AlarmDataviewRow>[] = [];
          alarmResponse
            .filter((alarm: AlarmDto) => this.isCurrentAlarm(alarm))
            .filter((alarm: AlarmDto) => !alarmTypeFilter.includes(alarm.alarm_type))
            .sort((firstAlarm: AlarmDto, secondAlarm: AlarmDto) =>
              new Date(secondAlarm.created_at).getTime() - new Date(firstAlarm.created_at).getTime(),
            ).map((alarm: AlarmDto) => {
            let device: DeviceDto | undefined = devices.find((device: DeviceDto) => device.id === alarm.device_id);
            if(!device) return;

            dataViewRowNew.push(this.mapAlarmToDataViewRow(alarm, device));
          });

          this.dataViewRow = dataViewRowNew;

          this.loading = false;
        },
        error: (error: unknown) => {
          console.error('Error fetching current alarms:', error);
          this.dataViewRow = [];
          this.loading = false;
        },
      });
  }

  private isCurrentAlarm(alarm: AlarmDto): boolean {
    return alarm.status === AlarmStatus.ACTIVE
      || alarm.status === AlarmStatus.ACKNOWLEDGED;
  }

  private mapAlarmToDataViewRow(
    alarm: AlarmDto,
    device: DeviceDto,
  ): DataviewRowInterface<AlarmDataviewRow> {
    const deviceSerialNumber: string = this._deviceService.getDeviceNumberByDevice(device);

    return {
      iconLeft: getMarkerStyleFromAlarm(alarm),
      rightTag: {
        name: this._translateService.instant('common.enum.alarm-severity.' + alarm.severity),
        severity: this._alarmService.mapAlarmSeverityClass(alarm.severity),
      },
      row: {
        id: alarm.id,
        serialNumber: deviceSerialNumber,
        title: alarm.title ?? '-',
        created_at: formatTimeAgoTranslation(this._translateService, alarm.created_at),
      },
    };
  }

  private createAlarmMarkerDevice(
    alarm: AlarmDto,
    device: DeviceDto | null,
    deviceSerialNumber: string,
  ): DeviceDto {
    if (device) {
      return {
        ...device,
        alarms: [alarm],
        active_alarms: [alarm],
      };
    }

    return {
      id: alarm.device_id,
      device_eui: alarm.device?.device_eui ?? deviceSerialNumber,
      serial_number: deviceSerialNumber,
      status: alarm.device?.status ?? DeviceStatus.ONLINE,
      is_archived: false,
      created_at: alarm.created_at ?? '',
      updated_at: alarm.updated_at ?? '',
      last_measurement: {},
      alarms: [alarm],
      active_alarms: [alarm],
    };
  }
}
